import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SharedModule } from '../shared.module';
import { MissionService } from '../../services/mission.service';
import { EstablishmentService } from '../../services/establishment.service';
import { FlagService } from '../services/flag.service';
import { FlagType } from '../utils/enums';
import { PermissionService } from '../../services/permission.service';
import { forkJoin } from 'rxjs';

type ContractType = 'MISSION' | 'DISPOSITION';

interface ContractPreviewDialogData {
  missionId: number;
  candidatId: number;
  mission: any;
  applicationId: number;
}

@Component({
  selector: 'app-contract-preview-dialog',
  templateUrl: './contract-preview-dialog.component.html',
  styleUrl: './contract-preview-dialog.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatTabsModule,
    SharedModule,
    DatePipe
  ]
})
export class ContractPreviewDialogComponent implements OnInit {
  /** Separate form for Mission contract — populated from ContractSnapshot */
  missionForm: FormGroup;
  /** Separate form for Disposition contract — populated from ContractSnapshot */
  dispositionForm: FormGroup;

  missionPdfSrc: SafeResourceUrl | null = null;
  dispositionPdfSrc: SafeResourceUrl | null = null;

  loadingMissionPdf = false;
  loadingDispositionPdf = false;
  savingMission = false;
  savingDisposition = false;
  sendingMission = false;
  sendingDisposition = false;
  openingDocuSignMission = false;
  openingDocuSignDisposition = false;

  /** Independent sent tracking */
  missionContractSent = false;
  dispositionContractSent = false;
  
  /** Independent signed tracking */
  missionContractSigned = false;
  dispositionContractSigned = false;

  /** Reminder config — loaded from tracker */
  missionReminderEnabled = false;
  missionReminderIntervalHours = 24;
  missionLastReminderSentAt: string | null = null;
  missionContractSentAt: string | null = null;
  missionNextReminderAt: string | null = null;
  missionNextSendInHours = 24;
  sendingMissionReminder = false;
  savingMissionReminder = false;

  dispositionReminderEnabled = false;
  dispositionReminderIntervalHours = 24;
  dispositionLastReminderSentAt: string | null = null;
  dispositionContractSentAt: string | null = null;
  dispositionNextReminderAt: string | null = null;
  dispositionNextSendInHours = 24;
  sendingDispositionReminder = false;
  savingDispositionReminder = false;

  /** Loading snapshots */
  loadingFreshData = true;

  activeTab: ContractType = 'MISSION';

  /** Snapshot raw data for doctor / sector fallback checks */
  private missionSnapshotRaw: any = null;
  private fullMission: any = null;

  /**
   * Lot F — CAS 1 (client clinique) : mission du secteur « Médecins » (typeSecteurId = 1).
   * Le contrat médecin est généré et archivé automatiquement côté back : visualisation seule —
   * champs readonly, pas de « Valider et envoyer », pas de rappels, pas de DocuSign.
   */
  get isCas1Doctor(): boolean {
    // Every source is checked independently: a source holding an unexpected shape (e.g. a typeSecteur object without
    // `name`) must not hide the others, otherwise a doctor contract shows the DocuSign / send actions.
    const sources: any[] = [this.fullMission, this.data?.mission].filter(Boolean);
    for (const m of sources) {
      const ids = [m.typeSecteurId, m.typePoste?.typeSecteur?.id, m.typeSecteur?.id, m.typeSecteur];
      if (ids.some((id) => id != null && typeof id !== 'object' && Number(id) === 1)) return true;
      const labels = [m.typeSecteur?.name, m.typeSecteur, m.typeSecteurName, m.typePoste?.typeSecteur?.name];
      if (labels.some((l) => this.isDoctorSectorLabel(l))) return true;
    }
    if (Number(this.missionSnapshotRaw?.missionTypeDeSecteurId) === 1) return true;
    if (this.isDoctorSectorLabel(this.missionSnapshotRaw?.missionTypeDeSecteur)) return true;

    const candName = String(
      this.missionForm?.get('candidatFullname')?.value ??
      this.missionForm?.get('candidatLastname')?.value ??
      (this.data?.mission as any)?.candidatName ?? ''
    ).trim().toLowerCase();
    if (candName.includes('doctor') || candName.includes('dr ') || candName.startsWith('dr.')) return true;

    return false;
  }

  private isDoctorSectorLabel(label: unknown): boolean {
    if (typeof label !== 'string') return false;
    const l = label.trim().toLowerCase();
    return l.startsWith('médecin') || l.startsWith('medecin');
  }

  /** Modifier / envoyer / relancer un contrat relève de « Répondre aux missions » (MISSION_VIEW). Le back reste l'autorité. */
  get canAct(): boolean {
    return this.permissionService.has('MISSION_VIEW');
  }

  constructor(
    public dialogRef: MatDialogRef<ContractPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ContractPreviewDialogData,
    private fb: FormBuilder,
    private missionService: MissionService,
    private establishmentService: EstablishmentService,
    private flag: FlagService,
    private sanitizer: DomSanitizer,
    private permissionService: PermissionService
  ) {
    // Initialize with empty forms; will be populated from snapshots
    this.missionForm = this.createContractForm({});
    this.dispositionForm = this.createContractForm({});
  }

  ngOnInit(): void {
    if (this.data?.missionId) {
      this.missionService.getMissionById(String(this.data.missionId)).subscribe({
        next: (mission) => {
          if (mission) this.fullMission = mission;
        },
        error: () => {}
      });
    }
    this.loadSnapshotsAndInit();
  }

  /**
   * Fetch snapshot data for BOTH contract types on dialog open.
   * The backend creates a snapshot from Mission data on first invocation.
   * Each contract type gets its own independent snapshot.
   */
  private loadSnapshotsAndInit(): void {
    this.loadingFreshData = true;

    // Load both snapshots + tracker status in parallel
    forkJoin({
      missionSnapshot: this.missionService.getContractSnapshot(this.data.missionId, this.data.candidatId, 'MISSION', this.data.applicationId),
      dispositionSnapshot: this.missionService.getContractSnapshot(this.data.missionId, this.data.candidatId, 'DISPOSITION', this.data.applicationId),
      trackerStatus: this.missionService.getContractTrackerStatus(this.data.missionId, this.data.candidatId, this.data.applicationId)
    }).subscribe({
      next: ({ missionSnapshot, dispositionSnapshot, trackerStatus }) => {
        this.missionSnapshotRaw = missionSnapshot;
        // Populate each form from its own snapshot
        this.missionForm = this.createContractForm(missionSnapshot);
        this.dispositionForm = this.createContractForm(dispositionSnapshot);

        // Set sent status from tracker
        this.missionContractSent = trackerStatus?.missionContractStatus === 'SENT' || trackerStatus?.missionContractStatus === 'SIGNED';
        this.dispositionContractSent = trackerStatus?.dispositionContractStatus === 'SENT' || trackerStatus?.dispositionContractStatus === 'SIGNED';

        // Set signed status
        this.missionContractSigned = trackerStatus?.missionContractStatus === 'SIGNED';
        this.dispositionContractSigned = trackerStatus?.dispositionContractStatus === 'SIGNED';

        // Load reminder config
        this.missionReminderEnabled = !!trackerStatus?.missionReminderEnabled;
        this.missionReminderIntervalHours = trackerStatus?.missionReminderIntervalHours ?? 24;
        this.missionLastReminderSentAt = trackerStatus?.missionLastReminderSentAt ?? null;
        this.missionContractSentAt = trackerStatus?.missionContractSentAt ?? null;
        this.missionNextReminderAt = trackerStatus?.missionNextReminderAt ?? null;
        this.dispositionReminderEnabled = !!trackerStatus?.dispositionReminderEnabled;
        this.dispositionReminderIntervalHours = trackerStatus?.dispositionReminderIntervalHours ?? 24;
        this.dispositionLastReminderSentAt = trackerStatus?.dispositionLastReminderSentAt ?? null;
        this.dispositionContractSentAt = trackerStatus?.dispositionContractSentAt ?? null;
        this.dispositionNextReminderAt = trackerStatus?.dispositionNextReminderAt ?? null;
        // Compute initial "next send in X hours" — prioritize nextReminderAt override, then last+interval
        this.missionNextSendInHours = this.computeRemainingHours(
          this.missionNextReminderAt, this.missionLastReminderSentAt, this.missionReminderIntervalHours);
        this.dispositionNextSendInHours = this.computeRemainingHours(
          this.dispositionNextReminderAt, this.dispositionLastReminderSentAt, this.dispositionReminderIntervalHours);

        this.loadingFreshData = false;

        // Load PDF previews SEQUENTIALLY (Jasper 6.8 has concurrency bugs in image rendering)
        this.loadPreview('MISSION', () => this.loadPreview('DISPOSITION'));
      },
      error: () => {
        this.flag.displayFlag('Erreur lors du chargement des données du contrat', FlagType.error);
        this.loadingFreshData = false;

        // Fallback: use the mission data passed in
        const m = this.data.mission ?? {};
        this.missionForm = this.createContractForm(m);
        this.dispositionForm = this.createContractForm(m);

        this.loadPreview('MISSION', () => this.loadPreview('DISPOSITION'));
      }
    });
  }

  /** Every field that the JRXML reads — mirrors ContractSnapshot. */
  private createContractForm(s: any): FormGroup {
    const t = (v: any) => (v && typeof v === 'string' ? v.length === 5 ? v : v.substring(0, 5) : v ?? '');
    const d = (v: any) => v ?? '';
    // Only fields actually referenced by the JRXML templates (contrat.jrxml and contrat-disposition.jrxml).
    // Removed: missionTypeDeSecteur, missionServiceAffectation, etablissementPays, etablissementType, candidatPays
    // (declared in DB/snapshot but never rendered in either contract PDF).
    return this.fb.group({
      // Mission
      compensationproposednet: [s.compensationproposednet ?? s.compensationProposedNet ?? 0],
      startdate: [d(s.startdate ?? s.startDate)],
      enddate: [d(s.enddate ?? s.endDate)],
      starttime: [t(s.starttime ?? s.startTime)],
      endtime: [t(s.endtime ?? s.endTime)],
      description: [d(s.description)],
      missionTitle: [d(s.missionTitle)],
      missionTypeDePoste: [d(s.missionTypeDePoste)],
      remunerationLegislation: [d(s.remunerationLegislation)],

      // Établissement
      etablissementName: [d(s.etablissementName)],
      etablissementAdress: [d(s.etablissementAdress)],
      etablissementCodePostale: [d(s.etablissementCodePostale)],
      etablissementVille: [d(s.etablissementVille)],
      etablissementSiret: [d(s.etablissementSiret)],
      etablissementNumResponsable: [d(s.etablissementNumResponsable)],
      etablissementContact: [d(s.etablissementContact)],

      // Candidat
      candidatFullname: [d(s.candidatFullname)],
      candidatFirstname: [d(s.candidatFirstname)],
      candidatLastname: [d(s.candidatLastname)],
      candidatAdress: [d(s.candidatAdress)],
      candidatPostalCode: [d(s.candidatPostalCode)],
      candidatVille: [d(s.candidatVille)],
      candidatTel: [d(s.candidatTel)],
      candidatNationality: [d(s.candidatNationality)],
      candidatSocialSecurity: [d(s.candidatSocialSecurity)],
      candidatBirthDay: [d(s.candidatBirthDay)],

      // Agence
      agencyName: [d(s.agencyName)],
      agencyAddress: [d(s.agencyAddress)],
      agencySiret: [d(s.agencySiret)],
      agencyPhone: [d(s.agencyPhone)]
    });
  }

  /** Returns the correct form for the given contract type */
  getForm(type: ContractType): FormGroup {
    return type === 'MISSION' ? this.missionForm : this.dispositionForm;
  }

  onTabChange(index: number): void {
    this.activeTab = index === 0 ? 'MISSION' : 'DISPOSITION';
  }

  loadPreview(type: ContractType, done?: () => void): void {
    if (type === 'MISSION') {
      this.loadingMissionPdf = true;
    } else {
      this.loadingDispositionPdf = true;
    }

    this.missionService.previewContract(this.data.missionId, this.data.candidatId, type, this.data.applicationId).subscribe({
      next: (res: any) => {
        const base64 = res?.contractBytes ?? res?.pdfBase64 ?? res;
        if (base64) {
          const url = this.base64ToBlobUrl(base64);
          const safe = this.sanitizer.bypassSecurityTrustResourceUrl(url);
          if (type === 'MISSION') {
            this.missionPdfSrc = safe;
          } else {
            this.dispositionPdfSrc = safe;
          }
        }

        // Update tracker status from preview response
        if (res?.missionContractStatus) {
          this.missionContractSent = res.missionContractStatus === 'SENT' || res.missionContractStatus === 'SIGNED';
          this.missionContractSigned = res.missionContractStatus === 'SIGNED';
        }
        if (res?.dispositionContractStatus) {
          this.dispositionContractSent = res.dispositionContractStatus === 'SENT' || res.dispositionContractStatus === 'SIGNED';
          this.dispositionContractSigned = res.dispositionContractStatus === 'SIGNED';
        }

        if (type === 'MISSION') this.loadingMissionPdf = false;
        else this.loadingDispositionPdf = false;
        done?.();
      },
      error: () => {
        this.flag.displayFlag('Erreur lors du chargement du contrat', FlagType.error);
        if (type === 'MISSION') this.loadingMissionPdf = false;
        else this.loadingDispositionPdf = false;
        done?.();
      }
    });
  }

  saveEdits(type: ContractType): void {
    if (type === 'MISSION') this.savingMission = true;
    else this.savingDisposition = true;

    const form = this.getForm(type);
    const raw = form.value;
    const clean: any = {
      missionId: this.data.missionId,
      candidatId: this.data.candidatId,
      applicationId : this.data.applicationId,
      contractType: type
    };
    Object.keys(raw).forEach(k => {
      const v = raw[k];
      if (v !== null && v !== undefined && v !== '') {
        clean[k] = v;
      }
    });

    this.missionService.saveContractEdits(clean).subscribe({
      next: () => {
        this.flag.displayFlag('Modifications enregistrées', FlagType.success);
        this.loadPreview(type);
        if (type === 'MISSION') this.savingMission = false;
        else this.savingDisposition = false;
      },
      error: () => {
        this.flag.displayFlag('Erreur lors de l\'enregistrement', FlagType.error);
        if (type === 'MISSION') this.savingMission = false;
        else this.savingDisposition = false;
      }
    });
  }

  validateAndSend(type: ContractType): void {
    if (type === 'MISSION') {
      this.sendingMission = true;
      this.missionService.validateSendMissionContract(this.data.missionId, this.data.candidatId, this.data.applicationId).subscribe({
        next: () => {
          this.flag.displayFlag('Contrat mission envoyé avec succès', FlagType.success);
          this.sendingMission = false;
          this.missionContractSent = true;
          this.missionService.toggleRefreshMission?.();
          // Don't auto-close — let the user also handle the disposition contract
          this.checkAndMaybeClose();
        },
        error: () => {
          this.flag.displayFlag('Erreur lors de l\'envoi du contrat mission', FlagType.error);
          this.sendingMission = false;
        }
      });
    } else {
      this.sendingDisposition = true;
      this.missionService.validateSendDispositionContract(this.data.missionId, this.data.candidatId, this.data.applicationId).subscribe({
        next: () => {
          this.flag.displayFlag('Contrat de mise à disposition envoyé avec succès', FlagType.success);
          this.sendingDisposition = false;
          this.dispositionContractSent = true;
          this.missionService.toggleRefreshMission?.();
          // Open the disposition contract in DocuSign
          // this.openDispositionInDocuSign();
          // Don't auto-close — let the user also handle the mission contract
          this.checkAndMaybeClose();
        },
        error: () => {
          this.flag.displayFlag('Erreur lors de l\'envoi du contrat de mise à disposition', FlagType.error);
          this.sendingDisposition = false;
        }
      });
    }
  }

  /**
   * Open the disposition contract in DocuSign directly from the stored envelope.
   * Called automatically after sending the disposition contract.
   * Opens in a new window with embedded viewer.
   */
  private openDispositionInDocuSign(): void {
    this.openingDocuSignDisposition = true;
    this.missionService.getDispositionDocuSignUrl(this.data.missionId, this.data.candidatId, this.data.applicationId).subscribe({
      next: (d: any) => {
        this.openingDocuSignDisposition = false;
        if (d.url) {
          // Open in a new window with embedded iframe and professional loader
          const newWindow = window.open('', '_blank', 'width=1200,height=800');
          if (newWindow) {
            newWindow.document.write(`
              <!DOCTYPE html>
              <html>
                <head>
                  <title>Collary - Contrat de mise à disposition</title>
                  <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
                    .container { width: 100%; height: 100vh; display: flex; flex-direction: column; }
                    .header { 
                      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                      color: white; 
                      padding: 20px;
                      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                    }
                    .header-left { display: flex; align-items: center; gap: 12px; }
                    .header h1 { font-size: 18px; font-weight: 600; }
                    .loader { 
                      width: 24px; 
                      height: 24px; 
                      border: 3px solid rgba(255,255,255,0.3);
                      border-top: 3px solid white;
                      border-radius: 50%;
                      animation: spin 1s linear infinite;
                    }
                    @keyframes spin { 
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                    .status-text { 
                      font-size: 14px; 
                      font-weight: 500;
                      opacity: 0.95;
                    }
                    .iframe-container { flex: 1; overflow: hidden; position: relative; }
                    iframe { width: 100%; height: 100%; border: none; }
                    .loading-overlay {
                      position: absolute;
                      top: 0;
                      left: 0;
                      right: 0;
                      bottom: 0;
                      background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      backdrop-filter: blur(2px);
                      z-index: 10;
                    }
                    .loading-spinner {
                      text-align: center;
                    }
                    .spinner-ring {
                      width: 50px;
                      height: 50px;
                      border: 4px solid rgba(102, 126, 234, 0.2);
                      border-top: 4px solid #667eea;
                      border-radius: 50%;
                      animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
                      margin: 0 auto 15px;
                    }
                    .loading-text {
                      color: #667eea;
                      font-size: 14px;
                      font-weight: 500;
                    }
                  </style>
                </head>
                <body>
                  <div class="container">
                    <div class="header">
                      <div class="header-left">
                        <div class="loader"></div>
                        <div>
                          <h1>🤝 Collary - Contrat de mise à disposition</h1>
                          <div class="status-text">Chargement du contrat...</div>
                        </div>
                      </div>
                    </div>
                    <div class="iframe-container">
                      <div class="loading-overlay">
                        <div class="loading-spinner">
                          <div class="spinner-ring"></div>
                          <div class="loading-text">Veuillez patienter...</div>
                        </div>
                      </div>
                      <iframe src="${d.url}" allow="camera *; microphone *; autoplay *; clipboard-write *" onload="document.querySelector('.loading-overlay').style.display='none'; document.querySelector('.status-text').textContent='Contrat prêt';" ></iframe>
                    </div>
                  </div>
                </body>
              </html>
            `);
          } else {
            this.flag.displayFlag('Impossible d\'ouvrir une nouvelle fenêtre. Vérifiez les paramètres du navigateur.', FlagType.warning);
          }
        } else {
          this.flag.displayFlag('Impossible de générer l\'URL DocuSign pour le contrat de mise à disposition', FlagType.warning);
        }
      },
      error: () => {
        this.openingDocuSignDisposition = false;
        this.flag.displayFlag('Erreur lors de l\'ouverture du contrat de mise à disposition dans DocuSign', FlagType.error);
      }
    });
  }

  /**
   * Open the contract directly in DocuSign (MISSION) or view the generated PDF (DISPOSITION).
   * Mirrors the legacy "Voir le contrat" / "Voir contract de disposition" actions that were
   * previously surfaced as icons in the mission action menu.
   */
  openInDocuSign(type: ContractType): void {
    const establishmentId = this.data.mission?.etablissementId ?? this.data.mission?.establishmentId;
    if (!establishmentId) {
      this.flag.displayFlag('Identifiant établissement manquant', FlagType.error);
      return;
    }

    if (type === 'MISSION') {
      this.openingDocuSignMission = true;
      let apiCall : any;
      if(!!this.data.applicationId){
        apiCall = this.establishmentService.getPreviewFragmentUrl(this.data.candidatId, establishmentId, this.data.applicationId);
      } else {
        apiCall = this.missionService.SignerMission(this.data.candidatId, this.data.missionId, establishmentId);
      }
      apiCall
      .subscribe({
        next: (url: any) => {
          this.openingDocuSignMission = false;
          if (url) {
            // Open in a new window with embedded iframe and professional loader
            const newWindow = window.open('', '_blank', 'width=1200,height=800');
            if (newWindow) {
              newWindow.document.write(`
                <!DOCTYPE html>
                <html>
                  <head>
                    <title>DocuSign - Contrat Mission</title>
                    <style>
                      * { margin: 0; padding: 0; box-sizing: border-box; }
                      body { overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
                      .container { width: 100%; height: 100vh; display: flex; flex-direction: column; }
                      .header { 
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        color: white; 
                        padding: 20px;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                      }
                      .header-left { display: flex; align-items: center; gap: 12px; }
                      .header h1 { font-size: 18px; font-weight: 600; }
                      .loader { 
                        width: 24px; 
                        height: 24px; 
                        border: 3px solid rgba(255,255,255,0.3);
                        border-top: 3px solid white;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                      }
                      @keyframes spin { 
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                      .status-text { 
                        font-size: 14px; 
                        font-weight: 500;
                        opacity: 0.95;
                      }
                      .iframe-container { flex: 1; overflow: hidden; position: relative; }
                      iframe { width: 100%; height: 100%; border: none; }
                      .loading-overlay {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        backdrop-filter: blur(2px);
                        z-index: 10;
                      }
                      .loading-spinner {
                        text-align: center;
                      }
                      .spinner-ring {
                        width: 50px;
                        height: 50px;
                        border: 4px solid rgba(102, 126, 234, 0.2);
                        border-top: 4px solid #667eea;
                        border-radius: 50%;
                        animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
                        margin: 0 auto 15px;
                      }
                      .loading-text {
                        color: #667eea;
                        font-size: 14px;
                        font-weight: 500;
                      }
                    </style>
                  </head>
                  <body>
                    <div class="container">
                      <div class="header">
                        <div class="header-left">
                          <div class="loader"></div>
                          <div>
                            <h1>📄 Collary - Contrat Mission</h1>
                            <div class="status-text">Chargement du contrat...</div>
                          </div>
                        </div>
                      </div>
                      <div class="iframe-container">
                        <div class="loading-overlay">
                          <div class="loading-spinner">
                            <div class="spinner-ring"></div>
                            <div class="loading-text">Veuillez patienter...</div>
                          </div>
                        </div>
                        <iframe src="${url}" allow="camera *; microphone *; autoplay *; clipboard-write *" onload="document.querySelector('.loading-overlay').style.display='none'; document.querySelector('.status-text').textContent='Contrat prêt';"></iframe>
                      </div>
                    </div>
                  </body>
                </html>
              `);
            } else {
              this.flag.displayFlag('Impossible d\'ouvrir une nouvelle fenêtre. Vérifiez les paramètres du navigateur.', FlagType.warning);
            }
          } else {
            this.flag.displayFlag('Aucun contrat disponible à afficher', FlagType.warning);
          }
        },
        error: () => {
          this.openingDocuSignMission = false;
          this.flag.displayFlag('Impossible de récupérer le contrat mission', FlagType.error);
        }
      });
    } else {
      this.openingDocuSignDisposition = true;
      this.openDispositionInDocuSign();
      // this.establishmentService
      //   .generateContractDisposition(this.data.candidatId, this.data.missionId, establishmentId)
      //   .subscribe({
      //     next: (res: any) => {
      //       this.openingDocuSignDisposition = false;
      //       if (res?.contractBytes) {
      //         const url = this.base64ToBlobUrl(res.contractBytes);
      //         window.open(url, '_blank', 'noopener, noreferrer');
      //       } else {
      //         this.flag.displayFlag('Aucun contrat de mise à disposition disponible', FlagType.warning);
      //       }
      //     },
      //     error: () => {
      //       this.openingDocuSignDisposition = false;
      //       this.flag.displayFlag('Impossible de récupérer le contrat de mise à disposition', FlagType.error);
      //     }
      //   });
    }
  }

  /**
   * Hours remaining until the next scheduled reminder.
   * - nextReminderAt explicit override wins (one-shot)
   * - else lastSentAt + interval
   * - else interval (default for never-sent)
   */
  private computeRemainingHours(nextReminderAt: string | null, lastSentAt: string | null, intervalHours: number): number {
    if (nextReminderAt) {
      const remainH = Math.round((new Date(nextReminderAt).getTime() - Date.now()) / 3600_000);
      return Math.max(1, remainH);
    }
    if (!lastSentAt) return intervalHours;
    const remainH = Math.round((new Date(lastSentAt).getTime() + intervalHours * 3600_000 - Date.now()) / 3600_000);
    return Math.max(1, remainH);
  }

  /** Computed next-reminder fire time based on the editable nextSendInHours input. */
  getNextReminderDate(type: 'MISSION' | 'DISPOSITION'): Date | null {
    const hours = type === 'MISSION' ? this.missionNextReminderAt : this.dispositionNextReminderAt;
    const h = type === 'MISSION' ? this.missionNextSendInHours : this.dispositionNextSendInHours;
    // if (!hours || hours <= 0) return null;
    // return new Date(Date.now() + hours * 3600_000);
    return new Date(hours ?? Date.now() + h * 3600_000);
  }

  saveReminderConfig(type: 'MISSION' | 'DISPOSITION'): void {
    if (type === 'MISSION') this.savingMissionReminder = true;
    else this.savingDispositionReminder = true;

    const enabled = type === 'MISSION' ? this.missionReminderEnabled : this.dispositionReminderEnabled;
    // Recurring interval and one-shot "next send" are INDEPENDENT
    const intervalHours = type === 'MISSION' ? this.missionReminderIntervalHours : this.dispositionReminderIntervalHours;
    const nextIn = type === 'MISSION' ? this.missionNextSendInHours : this.dispositionNextSendInHours;
    
    this.missionService.setContractReminderConfig(this.data.missionId, this.data.applicationId, this.data.candidatId, type, enabled, Math.max(1, intervalHours), nextIn)
      .subscribe({
        next: () => {
          this.flag.displayFlag('Configuration enregistrée', FlagType.success);
          if (type === 'MISSION') this.savingMissionReminder = false;
          else this.savingDispositionReminder = false;
        },
        error: () => {
          this.flag.displayFlag('Erreur lors de la sauvegarde', FlagType.error);
          if (type === 'MISSION') this.savingMissionReminder = false;
          else this.savingDispositionReminder = false;
        }
      });
  }

  sendReminderNow(type: 'MISSION' | 'DISPOSITION'): void {
    if (type === 'MISSION') this.sendingMissionReminder = true;
    else this.sendingDispositionReminder = true;

    this.missionService.sendContractReminderNow(this.data.missionId, this.data.candidatId, type, this.data.applicationId)
      .subscribe({
        next: () => {
          const msg = type === 'MISSION'
            ? 'Rappel envoyé au candidat'
            : 'Rappel envoyé à l\'établissement';
          this.flag.displayFlag(msg, FlagType.success);
          const now = new Date().toISOString();
          if (type === 'MISSION') { this.missionLastReminderSentAt = now; this.sendingMissionReminder = false; }
          else { this.dispositionLastReminderSentAt = now; this.sendingDispositionReminder = false; }
        },
        error: () => {
          this.flag.displayFlag('Erreur lors de l\'envoi du rappel', FlagType.error);
          if (type === 'MISSION') this.sendingMissionReminder = false;
          else this.sendingDispositionReminder = false;
        }
      });
  }

  /** Auto-close only when BOTH contracts are sent */
  private checkAndMaybeClose(): void {
    if (this.missionContractSent && this.dispositionContractSent) {
      this.dialogRef.close({ sent: 'BOTH' });
    }
  }

  close(): void {
    const result: any = {};
    if (this.missionContractSent) result.missionSent = true;
    if (this.dispositionContractSent) result.dispositionSent = true;
    this.dialogRef.close(Object.keys(result).length > 0 ? result : undefined);
  }

  private base64ToBlobUrl(base64: string): string {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  }
}
